import { useEffect, useMemo, useState } from "react";

import { useLocation } from "react-router";

import { Loader } from "simplydo/react/ui/loader";
import { usePrefersReducedMotion } from "simplydo/react/ui/utils";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import { useDocumentLang } from "utils/useDocumentLang";
import util from "utils/utils";

import OrgTagChooser from "components/lib/Choosers/Tags/OrgTagChooser";
import AgreeToTerms from "components/lib/Entry/AgreeToTerms";
import InitialProfileSetup from "components/lib/Entry/InitialProfileSetup";
import VerifyEmail from "components/lib/Entry/VerifyEmail";

import AppWrap from "./AppWrap";
import AppRouting from "./app/AppRouting";
import CustomOnboarding from "./lib/CustomOnboarding";
import CompleteVerifyEmail from "./lib/Entry/CompleteVerifyEmail";
import CreateBusinessProfile from "./lib/Entry/CreateBusinessProfile";
import EnforceMFA from "./lib/Entry/EnforceMFA";

const AppOnboarding = () => {
  useDocumentLang();
  const user = useCurrentUser();
  const location = useLocation();

  const [resumingSession, setResumingSession] = useState(true);
  const userHasVerifiedEmail = util.hasVerifiedEmail(
    user,
    user?.ownerOrganisation,
  );
  const userHasSetUpMFA = util.hasSetUpMFA(user, user?.ownerOrganisation);

  useEffect(() => {
    const finishResuming = () => setResumingSession(false);
    setResumingSession(true);
    api.auth.resumeSession(finishResuming, finishResuming);
  }, []);

  const shouldShowTagChooser = useMemo(
    () => util.shouldShowInterestsChooser(user),
    [user],
  );

  const onboardScreen = useMemo(() => {
    if (resumingSession) {
      return {
        done: false,
        content: (
          <AppWrap
            key="load"
            hideName
            content={<Loader size="large" data-label="onboarding-loader" />}
          />
        ),
      };
    }
    if (location.pathname === "/verify-email") {
      return {
        done: false,
        content: (
          <AppWrap
            key="complete-verify"
            hideName
            excludeWelcomeText
            content={<CompleteVerifyEmail />}
          />
        ),
      };
    }
    if (user && !util.hasAgreedTerms(user)) {
      return {
        done: false,
        content: <AppWrap key="terms" content={<AgreeToTerms />} />,
      };
    }
    if (user && !userHasVerifiedEmail) {
      return {
        done: false,
        content: <AppWrap key="verify" content={<VerifyEmail />} />,
      };
    }
    if (user && !userHasSetUpMFA) {
      return {
        done: false,
        content: <AppWrap key="mfa" content={<EnforceMFA />} />,
      };
    }
    if (user && (!user.profile?.firstName || !user.profile?.lastName)) {
      return {
        done: false,
        content: <AppWrap key="profile" content={<InitialProfileSetup />} />,
      };
    }
    if (shouldShowTagChooser) {
      return {
        done: false,
        content: <AppWrap key="tags" fluid content={<OrgTagChooser />} />,
      };
    }
    if (user?.groupRequiringProfile) {
      return {
        done: false,
        content: (
          <AppWrap key="bizprofile" fluid content={<CreateBusinessProfile />} />
        ),
      };
    }
    if (user && !user.hasCompletedOrganisationOnboarding) {
      return {
        done: false,
        content: <AppWrap key="onboarding" content={<CustomOnboarding />} />,
      };
    }
    return {
      done: true,
      content: (
        <AppWrap
          key="load"
          hideName
          content={<Loader size="large" data-label="onboarding-loader" />}
        />
      ),
    };
  }, [
    user,
    resumingSession,
    shouldShowTagChooser,
    userHasVerifiedEmail,
    userHasSetUpMFA,
    location.pathname,
  ]);

  const reducedMotion = usePrefersReducedMotion();
  const fadeOutDurationMs = reducedMotion ? 0 : 500;
  const [onboardingVisible, setOnboardingVisible] = useState(true);
  const [onboardingMounted, setOnboardingMounted] = useState(true);
  useEffect(() => {
    if (onboardScreen.done) {
      setOnboardingVisible(false);
      const unmountTimeout = setTimeout(() => {
        setOnboardingMounted(false);
      }, fadeOutDurationMs);
      return () => clearTimeout(unmountTimeout);
    } else {
      setOnboardingVisible(true);
      setOnboardingMounted(true);
    }
  }, [onboardScreen.done, fadeOutDurationMs]);

  return (
    <>
      {onboardingMounted ? (
        <div
          data-label="onboarding-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
            backgroundColor: "#fff",
            opacity: onboardingVisible ? 1 : 0,
            pointerEvents: onboardingVisible ? "auto" : "none",
            transition: `opacity ${fadeOutDurationMs / 1000}s`,
          }}
        >
          {onboardScreen.content}
        </div>
      ) : null}
      {onboardScreen.done ? <AppRouting /> : null}
    </>
  );
};

export default AppOnboarding;
